/**
 * <contact-section> — contact form with required-checkbox gating of submit button.
 *
 * Loaded ONLY from `sections/section-contact_form.liquid` via
 * `type="module" fetchpriority="low"`.
 *
 * Dependencies:
 *   - @theme/delegate
 */

import { Delegate } from '@theme/delegate';

class ContactSection extends HTMLElement {
  #delegateElement = null;
  #form = null;
  #submitBtn = null;

  connectedCallback() {
    this.element = this;
    this.#delegateElement = new Delegate(this.element);
    this.#form = this.querySelector('form');
    this.#submitBtn = this.querySelector('.contact-section__btn');
    // Client-side validation owns the per-field error UI, so suppress native bubbles.
    // Without JS the form keeps native validation (progressive enhancement).
    this.#form?.setAttribute('novalidate', 'novalidate');
    this.bindEvents();
    this.toggleButton();
  }

  disconnectedCallback() {
    this.#submitBtn?.removeEventListener('click', this.#onSubmitAttempt);
    this.#form?.removeEventListener('submit', this.#onSubmitAttempt, true);
    this.#delegateElement?.destroy();
    this.#delegateElement = null;
  }

  bindEvents() {
    // Validate on the submit button CLICK (COI standard: switch-nails / care-of-gerd /
    // trust-care all gate the submit this way). Cancelling the click's default also
    // cancels the implicit submit, so the `submit` event never fires - which is why
    // this also neutralises Shopify's hCaptcha: it hooks `submit` and re-submits
    // programmatically after its challenge, and that hook simply never engages.
    this.#submitBtn?.addEventListener('click', this.#onSubmitAttempt);
    // Fallback for keyboard submit (Enter in a text field never clicks the button).
    // Capture so we pre-empt the captcha's bubble-phase submit hook on that path too.
    this.#form?.addEventListener('submit', this.#onSubmitAttempt, true);
    this.#delegateElement.on(
      'change',
      "[data-state='required']",
      this.toggleButton.bind(this)
    );
    // Clear a field's error the moment the user makes it valid again.
    this.#delegateElement.on('input', '.contact-field .input-placeholder', this.#onFieldInput);
  }

  // Flag every incorrectly-filled field (and only those) per Figma: red border +
  // inline message below the field. When anything is invalid, kill the event so the
  // submit (and the captcha that hooks it) never fires; focus the first bad field.
  #onSubmitAttempt = (event) => {
    if (!this.#form) return;

    // checkValidity() honours `required` (valueMissing) + type=email (typeMismatch).
    const fields = Array.from(this.#form.querySelectorAll('.contact-field .input-placeholder'));
    const invalid = fields.filter((field) => !field.checkValidity());

    fields.forEach((field) => {
      if (invalid.includes(field)) {
        this.#showFieldError(field, this.#errorMessage(field));
      } else {
        this.#hideFieldError(field);
      }
    });

    // Reflect the new error state on the submit button (disable while errors show).
    this.toggleButton();

    if (invalid.length > 0) {
      // preventDefault cancels the submit; stopPropagation keeps the event from
      // reaching Shopify's captcha submit hook (it listens in the bubble phase).
      event.preventDefault();
      event.stopPropagation();
      invalid[0]?.focus();
    }
  };

  #onFieldInput = (_event, field) => {
    // Only re-check fields already flagged, so we never flag a field mid-typing.
    if (!field.closest('.field')?.classList.contains('field--error')) return;
    if (field.checkValidity()) {
      this.#hideFieldError(field);
      // Field is valid again — re-enable the submit button (if nothing else blocks it).
      this.toggleButton();
    }
  };

  // Localized message for an invalid field, with hard fallbacks so an empty data-* attr
  // never blanks the message. typeMismatch = bad email; otherwise it's an empty required.
  #errorMessage(field) {
    if (field.validity.typeMismatch) {
      return this.dataset.errorEmail || 'Please enter a valid email address';
    }
    return this.dataset.errorRequired || 'Please fill in this field';
  }

  #showFieldError(field, message) {
    field.closest('.field')?.classList.add('field--error');
    field.setAttribute('aria-invalid', 'true');
    const errorEl = field.closest('.contact-field')?.querySelector('[data-field-error]');
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
    if (errorEl.id) field.setAttribute('aria-describedby', errorEl.id);
  }

  #hideFieldError(field) {
    field.closest('.field')?.classList.remove('field--error');
    field.removeAttribute('aria-invalid');
    field.removeAttribute('aria-describedby');
    const errorEl = field.closest('.contact-field')?.querySelector('[data-field-error]');
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  toggleButton() {
    const btn = this.element.querySelector('.contact-section__btn');
    if (!btn) return;

    // In the Theme Editor keep the button enabled so merchants can preview the
    // chosen Button style (disabled state mutes/hides some variants).
    if (window.Shopify?.designMode) {
      btn.removeAttribute('disabled');
      return;
    }

    const allBoxes = this.element.querySelectorAll("[data-state='required']");
    const allChecked = Array.from(allBoxes).every((item) => item.checked);
    // Also disable while any field error is shown (e.g. the invalid-email message),
    // so the user can't re-submit until the flagged field is corrected.
    const hasFieldError = !!this.element.querySelector('.contact-field .field--error');

    if (allChecked && !hasFieldError) {
      btn.removeAttribute('disabled');
    } else {
      btn.setAttribute('disabled', true);
    }
  }
}

if (!customElements.get('contact-section')) {
  customElements.define('contact-section', ContactSection);
}
